/**
 * CalendarEventModal — Create / Edit event modal
 * Matches the elvoaq-calendar.html reference design.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { CalendarEvent, CalendarCategory } from '../hooks/useCalendarEvents';
import useI18n from '../hooks/useI18n';
import '../styles/Calendar.css';

const EVENT_COLORS = [
  '#7c5cfc', '#ff4d6a', '#00d4aa', '#ff8c42', '#4da6ff',
  '#ff6eb4', '#ffd166', '#a8ff78', '#6366f1', '#14b8a6',
];

interface CalendarEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<CalendarEvent, 'id' | 'createdAt'>) => void;
  onDelete?: (id: string) => void;
  initialDate?: string;
  event?: CalendarEvent | null;
  categories: CalendarCategory[];
}

const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialDate,
  event,
  categories,
}) => {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [color, setColor] = useState(EVENT_COLORS[0]);
  const [allDay, setAllDay] = useState(false);
  const [categoryId, setCategoryId] = useState('meetings');

  useEffect(() => {
    if (!isOpen) return;
    if (event) {
      setTitle(event.title);
      setDescription(event.description || '');
      setLocation(event.location || '');
      setDate(event.date);
      setStartTime(event.startTime);
      setEndTime(event.endTime);
      setColor(event.color);
      setAllDay(event.allDay);
      setCategoryId(event.categoryId);
    } else {
      setTitle('');
      setDescription('');
      setLocation('');
      setDate(initialDate || new Date().toISOString().split('T')[0]);
      setStartTime('09:00');
      setEndTime('10:00');
      setColor(EVENT_COLORS[0]);
      setAllDay(false);
      setCategoryId(categories[0]?.id || 'meetings');
    }
  }, [isOpen, event, initialDate, categories]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      date,
      startTime: allDay ? '00:00' : startTime,
      endTime: allDay ? '23:59' : endTime,
      color,
      allDay,
      categoryId,
    });
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="ecal-modal-overlay" onClick={onClose}>
      <div className="ecal-modal" onClick={e => e.stopPropagation()}>
        <div className="ecal-modal-header">
          <h3 className="ecal-modal-title">{event ? t('calendar.editEvent') : t('calendar.newEvent')}</h3>
          <button className="ecal-modal-close" onClick={onClose} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="ecal-form-group">
            <label className="ecal-form-label">{t('calendar.eventTitle')}</label>
            <input
              className="ecal-form-input"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={t('calendar.titlePlaceholder')}
              autoFocus
              required
            />
          </div>

          {/* Date + Category */}
          <div className="ecal-form-row">
            <div className="ecal-form-group">
              <label className="ecal-form-label">{t('calendar.date')}</label>
              <input
                className="ecal-form-input"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
              />
            </div>
            <div className="ecal-form-group">
              <label className="ecal-form-label">{t('calendar.category')}</label>
              <select
                className="ecal-form-input"
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.isDefault ? t(`calendar.cat.${cat.id}`) : cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* All Day Toggle */}
          <div className="ecal-form-group">
            <label className="ecal-allday-toggle">
              <input
                type="checkbox"
                checked={allDay}
                onChange={e => setAllDay(e.target.checked)}
              />
              <span>{t('calendar.allDay')}</span>
            </label>
          </div>

          {/* Start / End Times */}
          {!allDay && (
            <div className="ecal-form-row">
              <div className="ecal-form-group">
                <label className="ecal-form-label">{t('calendar.startTime')}</label>
                <input
                  className="ecal-form-input"
                  type="time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                />
              </div>
              <div className="ecal-form-group">
                <label className="ecal-form-label">{t('calendar.endTime')}</label>
                <input
                  className="ecal-form-input"
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Location */}
          <div className="ecal-form-group">
            <label className="ecal-form-label">{t('calendar.location')}</label>
            <input
              className="ecal-form-input"
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder={t('calendar.locationPlaceholder')}
            />
          </div>

          {/* Notes / Description */}
          <div className="ecal-form-group">
            <label className="ecal-form-label">{t('calendar.description')}</label>
            <textarea
              className="ecal-form-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('calendar.descriptionPlaceholder')}
              rows={2}
            />
          </div>

          {/* Color */}
          <div className="ecal-form-group">
            <label className="ecal-form-label">{t('calendar.color')}</label>
            <div className="ecal-color-picker">
              {EVENT_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`ecal-color-swatch${color === c ? ' selected' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="ecal-form-actions">
            {event && onDelete && (
              <button
                type="button"
                className="ecal-btn-danger"
                onClick={() => {
                  if (window.confirm(t('calendar.deleteConfirm'))) {
                    onDelete(event.id);
                    onClose();
                  }
                }}
              >
                {t('calendar.delete')}
              </button>
            )}
            <button type="button" className="ecal-btn-secondary" onClick={onClose}>
              {t('calendar.cancel')}
            </button>
            <button type="submit" className="ecal-btn-primary">
              {event ? t('calendar.save') : t('calendar.create')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CalendarEventModal;
